export interface Release {
  version: string;
  date: string | null;
  title: string;
  summary: string;
  body: string;
}

const releaseContext = require.context("../entries", false, /release-v.*\.md$/);

const versionFromFilename = (filename: string): string | null => {
  const match = filename.match(/release-v(.+)\.md$/);
  return match ? match[1] : null;
};

const parseFrontmatter = (raw: unknown): { meta: Record<string, string>; body: string } => {
  const source = typeof raw === "string" ? raw : (raw as { default?: string })?.default ?? "";
  const match = source.match(/^---\s*\n([\s\S]*?)\n---\s*\n?([\s\S]*)$/);

  if (!match) return { meta: {}, body: source.trim() };

  const meta: Record<string, string> = {};
  match[1].split("\n").forEach((line) => {
    const separatorIndex = line.indexOf(":");
    if (separatorIndex === -1) return;
    const key = line.slice(0, separatorIndex).trim();
    const value = line.slice(separatorIndex + 1).trim();
    if (key) meta[key] = value;
  });

  return { meta, body: match[2].trim() };
};

const compareVersionsDesc = (a: Release, b: Release): number => {
  const partsA = a.version.split(".").map(Number);
  const partsB = b.version.split(".").map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i += 1) {
    const diff = (partsB[i] ?? 0) - (partsA[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
};

export const releases: Release[] = releaseContext
  .keys()
  .map((key): Release | null => {
    const version = versionFromFilename(key);
    if (!version) return null;

    const { meta, body } = parseFrontmatter(releaseContext(key));

    return {
      version,
      date: meta.date ?? null,
      title: meta.title ?? `v${version}`,
      summary: meta.summary ?? "",
      body,
    };
  })
  .filter((release): release is Release => release !== null)
  .sort(compareVersionsDesc);

export const currentVersion: string = releases[0]?.version ?? "0.0.0";
